import { useEffect, useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { isAdminRole } from '@/lib/role';

const navItems = [
  { path: '/dashboard', label: '仪表盘' },
  { path: '/problems', label: '题目库' },
  { path: '/templates', label: '教学模板' },
  { path: '/templates/custom', label: '我的模板' },
  { path: '/generation-jobs', label: '生成记录' },
  { path: '/submissions', label: '提交记录' },
  { path: '/learning', label: '学习路径' },
];

const adminNavItems = [
  { path: '/admin/problems', label: '题目管理' },
  { path: '/admin/judge-agents', label: '评测 Agent' },
  { path: '/admin/templates', label: '模板管理' },
  { path: '/admin/generation-feedback', label: '反馈分析' },
];

export default function MainLayout() {
  const location = useLocation();
  const user = useAuthStore(state => state.user);
  const logout = useAuthStore(state => state.logout);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const visibleNavItems = isAdminRole(user?.role) ? [...navItems, ...adminNavItems] : navItems;

  const isItemActive = (path: string) => {
    if (path === '/templates/custom') {
      return location.pathname === '/templates/custom';
    }
    if (path === '/templates') {
      return (
        location.pathname === '/templates' ||
        (location.pathname.startsWith('/templates/') && !location.pathname.startsWith('/templates/custom'))
      );
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsAccountMenuOpen(false);
  }, [location.pathname]);

  const renderNavLink = (item: { path: string; label: string }, className: string) => (
    <Link
      key={item.path}
      to={item.path}
      className={className}
      onClick={() => setIsMobileMenuOpen(false)}
    >
      {item.label}
    </Link>
  );

  return (
    <div className="min-h-screen bg-apple-light-gray">
      {/* Apple glass navigation */}
      <nav
        className="sticky top-0 z-50"
        style={{
          background: 'rgba(29, 29, 31, 0.88)',
          backdropFilter: 'saturate(180%) blur(20px)',
          WebkitBackdropFilter: 'saturate(180%) blur(20px)',
        }}
      >
        <div className="max-w-[980px] mx-auto w-full h-12 px-4 sm:px-6 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="text-white font-semibold text-[17px] tracking-tight shrink-0">
            CodePilot
          </Link>

          {/* Nav links */}
          <div className="hidden lg:flex items-center gap-7">
            {visibleNavItems.map(item =>
              renderNavLink(
                item,
                `text-[12px] transition-colors whitespace-nowrap ${
                  isItemActive(item.path) ? 'text-white' : 'text-white/60 hover:text-white'
                }`
              )
            )}
          </div>

          {/* User info + logout */}
          <div className="relative flex items-center gap-3 shrink-0">
            <button
              type="button"
              aria-label={`${user?.name ?? '用户'} 账户菜单`}
              aria-controls="account-menu"
              aria-expanded={isAccountMenuOpen}
              onClick={() => setIsAccountMenuOpen(current => !current)}
              className="inline-flex h-8 items-center gap-2 rounded-full px-2.5 text-white/70 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/15 text-[11px] font-semibold text-white">
                {(user?.name?.charAt(0) || 'U').toUpperCase()}
              </span>
              <span className="hidden sm:inline max-w-[120px] truncate text-[12px]">
                {user?.name}
              </span>
              <span
                aria-hidden="true"
                className={`text-[10px] transition-transform ${isAccountMenuOpen ? 'rotate-180' : ''}`}
              >
                v
              </span>
            </button>
            {isAccountMenuOpen ? (
              <div
                id="account-menu"
                data-testid="account-menu"
                className="absolute right-0 top-10 z-50 w-52 overflow-hidden rounded-xl border border-white/10 bg-[#1d1d1f] py-1 shadow-card-sm"
              >
                <Link
                  to="/profile"
                  className="block px-3 py-2 text-[13px] text-white/80 hover:bg-white/10 hover:text-white"
                >
                  个人资料
                </Link>
                <Link
                  to="/settings/profile"
                  className="block px-3 py-2 text-[13px] text-white/80 hover:bg-white/10 hover:text-white"
                >
                  设置
                </Link>
                <Link
                  to="/settings/llm-providers"
                  className="block px-3 py-2 text-[13px] text-white/80 hover:bg-white/10 hover:text-white"
                >
                  LLM 设置
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setIsAccountMenuOpen(false);
                    logout();
                  }}
                  className="block w-full px-3 py-2 text-left text-[13px] text-white/60 hover:bg-white/10 hover:text-white"
                >
                  退出登录
                </button>
              </div>
            ) : null}
            <button
              type="button"
              aria-label={isMobileMenuOpen ? '关闭导航菜单' : '打开导航菜单'}
              aria-controls="mobile-navigation-menu"
              aria-expanded={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen(current => !current)}
              className="lg:hidden inline-flex h-8 w-8 items-center justify-center rounded-full text-white/80 hover:text-white hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            >
              <span className="sr-only">{isMobileMenuOpen ? '关闭导航菜单' : '打开导航菜单'}</span>
              <span className="relative h-3.5 w-4" aria-hidden="true">
                <span
                  className={`absolute left-0 top-1 block h-px w-4 bg-current transition-transform ${
                    isMobileMenuOpen ? 'translate-y-[3px] rotate-45' : ''
                  }`}
                />
                <span
                  className={`absolute left-0 top-2 block h-px w-4 bg-current transition-transform ${
                    isMobileMenuOpen ? '-translate-y-[3px] -rotate-45' : ''
                  }`}
                />
              </span>
            </button>
          </div>
        </div>

        {isMobileMenuOpen ? (
          <div
            id="mobile-navigation-menu"
            data-testid="mobile-navigation-menu"
            className="lg:hidden border-t border-white/10 px-4 pb-4 pt-2"
          >
            <div className="mx-auto grid max-w-[980px] grid-cols-1 gap-1">
              {visibleNavItems.map(item =>
                renderNavLink(
                  item,
                  `rounded-lg px-2 py-2 text-[15px] transition-colors ${
                    isItemActive(item.path)
                      ? 'bg-white/10 text-white'
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`
                )
              )}
            </div>
          </div>
        ) : null}
      </nav>

      {/* Page content */}
      <main>
        <Outlet />
      </main>
    </div>
  );
}
